import { useEffect, useMemo, useState } from "react";
import { useNotificationCenter } from "../contexts/NotificationContext";

/**
 * Hook to show an auto-dismiss snackbar when the user has unread **birthday**
 * notifications. Intentionally scoped to birthday-only so that system
 * notifications (orders, inventory, etc.) do NOT trigger the snackbar.
 *
 * Usage:
 * const { showSnackbar, snackbarMessage, closeSnackbar } = useBirthdayNotificationSnackbar();
 */
export const useBirthdayNotificationSnackbar = () => {
  const { birthDayNotifications } = useNotificationCenter();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [hasShown, setHasShown] = useState(false);

  // Derived count: only unread birthday notifications
  const unreadBirthdayCount = useMemo(
    () => birthDayNotifications.filter((n) => !n.isRead).length,
    [birthDayNotifications],
  );

  useEffect(() => {
    if (unreadBirthdayCount > 0 && !hasShown) {
      const message =
        unreadBirthdayCount === 1
          ? "You have 1 new birthday reminder! 🎂"
          : `You have ${unreadBirthdayCount} new birthday reminders! 🎂`;

      setSnackbarMessage(message);
      setShowSnackbar(true);
      setHasShown(true);


      // Auto-dismiss after 6 seconds
      const timer = setTimeout(() => {
        setShowSnackbar(false);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [unreadBirthdayCount, hasShown]);

  const closeSnackbar = () => {
    setShowSnackbar(false);
  };

  return {
    showSnackbar,
    snackbarMessage,
    closeSnackbar,
  };
};
